import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/lib/ThemeProvider";
import { kvGet, kvSet, isNative } from "@/lib/native";
import { api } from "@/lib/api";
import { ChevronLeft, Settings2, Moon, Sun, Type, ShieldCheck, Trash2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ZOOM_KEY = "stylio:app-zoom";

export default function AppSettings() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [zoom, setZoom] = useState(100);
  const [permsCamera, setPermsCamera] = useState(false);
  const [permsPush, setPermsPush] = useState(false);
  const [retention, setRetention] = useState(30);
  const [retentionMeta, setRetentionMeta] = useState({ min: 7, max: 365, default: 30 });

  useEffect(() => {
    (async () => {
      const z = await kvGet(ZOOM_KEY);
      if (z) setZoom(Number(z));
    })();
    // Load current scan-photo retention setting from backend
    (async () => {
      try {
        const { data } = await api.get("/beauty/scans/retention");
        setRetention(data.days);
        setRetentionMeta({ min: data.min, max: data.max, default: data.default });
      } catch { /* not logged in / endpoint missing — ignore */ }
    })();
  }, []);

  const saveRetention = async (v) => {
    const days = Array.isArray(v) ? v[0] : v;
    setRetention(days);
    try {
      await api.patch("/beauty/scans/retention", { days });
      toast.success(`Scan photos will auto-delete after ${days} days.`);
    } catch {
      toast.error("Couldn't save retention setting.");
    }
  };

  useEffect(() => {
    // Persist app zoom setting and apply globally — no reset on unmount so
    // the user's preferred zoom sticks across every screen.
    document.documentElement.style.fontSize = `${zoom}%`;
    kvSet(ZOOM_KEY, String(zoom)).catch(() => {});
  }, [zoom]);

  const persistZoom = async (v) => {
    setZoom(v[0]);
    await kvSet(ZOOM_KEY, String(v[0]));
  };

  const requestCameraPerm = async () => {
    if (!isNative()) return toast.message("Camera permission is granted by the browser on web.");
    try {
      const { Camera } = await import("@capacitor/camera");
      const r = await Camera.requestPermissions({ permissions: ["camera", "photos"] });
      setPermsCamera(r.camera === "granted");
      toast.success(r.camera === "granted" ? "Camera enabled" : "Camera denied");
    } catch { toast.error("Could not request camera permission"); }
  };

  const requestPushPerm = async () => {
    if (!isNative()) return toast.message("Push notifications need the native app.");
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const r = await PushNotifications.requestPermissions();
      setPermsPush(r.receive === "granted");
      toast.success(r.receive === "granted" ? "Notifications enabled" : "Notifications denied");
    } catch { toast.error("Could not request push permission"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><Settings2 size={12} /> Stylebay · App settings</p>
      <h1 className="editorial-h1 mt-3">App settings.</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">
        Theme, text size, and device permissions.
      </p>

      {/* Theme */}
      <section className="mt-10 border border-border p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
            <div>
              <p className="text-sm font-medium">Theme</p>
              <p className="text-xs text-muted-foreground mt-0.5 capitalize">{theme} mode</p>
            </div>
          </div>
          <Switch
            checked={theme === "dark"}
            onCheckedChange={toggle}
            data-testid="settings-theme-toggle"
          />
        </div>
      </section>

      {/* Zoom */}
      <section className="mt-4 border border-border p-6">
        <div className="flex items-center gap-3">
          <Type size={16} />
          <div className="flex-1">
            <p className="text-sm font-medium">Text size</p>
            <p className="text-xs text-muted-foreground mt-0.5">{zoom}%</p>
          </div>
        </div>
        <Slider
          value={[zoom]}
          min={85}
          max={130}
          step={5}
          onValueChange={persistZoom}
          className="mt-5"
          data-testid="settings-zoom-slider"
        />
        <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Compact</span><span>Default</span><span>Comfort</span>
        </div>
      </section>

      {/* Permissions */}
      <section className="mt-4 border border-border p-6">
        <p className="eyebrow flex items-center gap-2"><ShieldCheck size={12} /> Permissions</p>
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Camera & Photos</p>
              <p className="text-xs text-muted-foreground mt-0.5">Snap closet items and try-on photos.</p>
            </div>
            <Button variant="outline" onClick={requestCameraPerm} className="rounded-none uppercase tracking-[0.18em] text-[11px] h-9 px-4" data-testid="settings-perm-camera">
              {permsCamera ? "Granted" : "Allow"}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Push notifications</p>
              <p className="text-xs text-muted-foreground mt-0.5">Daily look brief, follower alerts.</p>
            </div>
            <Button variant="outline" onClick={requestPushPerm} className="rounded-none uppercase tracking-[0.18em] text-[11px] h-9 px-4" data-testid="settings-perm-push">
              {permsPush ? "Granted" : "Allow"}
            </Button>
          </div>
        </div>
      </section>

      {/* Beauty scan photo retention */}
      <section className="mt-4 border border-border p-6" data-testid="settings-scan-retention">
        <p className="eyebrow flex items-center gap-2"><Trash2 size={12} /> Beauty scan privacy</p>
        <div className="mt-4 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium">Auto-delete scan photos</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your selfie photos are permanently deleted after <span className="font-semibold text-foreground">{retention} days</span>.
              The AI analysis stays — only the raw photo is removed.
            </p>
          </div>
        </div>
        <Slider
          value={[retention]}
          min={retentionMeta.min}
          max={retentionMeta.max}
          step={1}
          onValueChange={(v) => setRetention(v[0])}
          onValueCommit={saveRetention}
          className="mt-5"
          data-testid="settings-retention-slider"
        />
        <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>{retentionMeta.min}d</span>
          <span>{retentionMeta.default}d default</span>
          <span>{retentionMeta.max}d</span>
        </div>
      </section>
    </div>
  );
}
